import React from 'react';
import { useHistory } from 'react-router-dom';
import { Button, ButtonSize } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';

import { OptionPresetGroupType } from '@/services/option-preset/option-preset-group';
import { useOptionConfigurator } from './OptionConfiguratorContext';
import { useShowroomActions } from './useShowroomActions';
import { buildConfiguratorUrl } from './utils/buildConfiguratorUrl';
import { ImmersiveSignAndSend } from './Room/components/ImmersiveSignAndSend';

export type PrimaryClose = { label: React.ReactNode; ariaLabel: string; onClick: () => void };
export type PrimaryComplete =
    | { kind: 'immersive'; disabled: boolean; viewOnly: boolean; onProceed: () => void }
    | { kind: 'showroom'; label: React.ReactNode; onClick: () => void };

export interface ConfiguratorPrimaryActions {
    close: PrimaryClose | null;
    complete: PrimaryComplete | null;
}

/**
 * Derives the configurator's primary navigation actions — close (back) and complete (sign-and-send /
 * host complete) — for the compact (immersive + showroom) shells. Shared by the compact top bar and
 * the mobile bottom action bar so the (non-trivial) disable + routing logic lives in one place.
 * Returns nulls for the `auth` mode, which uses its own bar.
 */
export function useConfiguratorPrimaryActions(
    mode: 'auth' | 'showroom' | 'immersive',
    backTo?: string
): ConfiguratorPrimaryActions {
    const history = useHistory();
    const { selectedDeadlineId, configuratorData, projectData, baseUrl, viewOnly } = useOptionConfigurator();
    const showroom = useShowroomActions();

    if (mode === 'auth') return { close: null, complete: null };

    const close: PrimaryClose | null =
        mode === 'immersive'
            ? backTo
                ? { label: <Trans>Back</Trans>, ariaLabel: t`Back`, onClick: () => history.push(backTo) }
                : null
            : showroom.closeLabel
              ? { label: showroom.closeLabel, ariaLabel: showroom.closeLabel, onClick: showroom.onClose }
              : null;

    const signBaseUrl = mode === 'immersive' ? baseUrl.replace(/\/configurator$/, '') : baseUrl;
    const handleSignClick = () => {
        if (!selectedDeadlineId) return;
        history.push(buildConfiguratorUrl({ baseUrl: signBaseUrl, deadlineId: selectedDeadlineId, segment: 'sign-pre' }));
    };
    const hasUnpickedForceSelection = configuratorData?.styleGroups.some((g) => {
        if (g.type === OptionPresetGroupType.Room && !g.roomId) return false;
        return g.forceSelection && !g.styles.some((s) => s.isPicked);
    });
    const selectedDeadline = projectData?.deadlines.find((d) => d.id === selectedDeadlineId);
    const isDeadlineExpired = selectedDeadline
        ? new Date(selectedDeadline.deadline) < new Date(new Date().toDateString())
        : false;
    const disableSignAndSend = !!hasUnpickedForceSelection || isDeadlineExpired;

    const complete: PrimaryComplete | null =
        mode === 'immersive'
            ? { kind: 'immersive', disabled: disableSignAndSend, viewOnly, onProceed: handleSignClick }
            : showroom.completeLabel
              ? { kind: 'showroom', label: showroom.completeLabel, onClick: showroom.onComplete }
              : null;

    return { close, complete };
}

/** Renders the resolved complete action — the immersive sign-and-send flow, or a showroom button. */
export const PrimaryCompleteControl: React.FC<{ complete: PrimaryComplete; size?: ButtonSize }> = ({
    complete,
    size,
}) => {
    if (complete.kind === 'immersive') {
        return (
            <ImmersiveSignAndSend
                disabled={complete.disabled}
                viewOnly={complete.viewOnly}
                size={size}
                onProceed={complete.onProceed}
            />
        );
    }
    return (
        <Button variant="primary" size={size} onClick={complete.onClick}>
            {complete.label}
        </Button>
    );
};
